import { PollOverlay, toSinglePollOverlayColor } from "@playlive/overlay-core/overlays/poll";
import { deepSignal } from "deepsignal";
import Picker from "@/components/input/picker.input.jsx";
import YesNoButtonInput from "@/components/input/yes-no-button.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import YesNoButtonInputWrapper from "@/components/overlays/wrapper/yes-no-button-input-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import useCommon from "@/hooks/useCommon.js";
import useTiltifyConfig from "@/hooks/useTiltifyConfig.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  pollsBackgroundColor: "multi",
  pollsType: "multiple",
  pollsShowDollars: "yes",
  pollsShowPercentage: "yes",
});

export default function PollOverlayPage() {
  const isMultiPoll = config.pollsType === "multiple";
  const tiltifyConfig = useTiltifyConfig();
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { setConfigState, overlayInfo, overlayURL } = useCommon({
    config,
    urlSuffix: tiltifyConfig.tiltifyPollID,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/polls" : "polls",
  });

  function filterColors(color) {
    if (isMultiPoll) {
      return !color.includes("grey") && !color.includes("black");
    }
    return !color.includes("grey") && !color.includes("black") && !color.includes("dark");
  }

  const pollOptions = [
    { name: "Multiple Options", value: "multiple" },
    { name: "Head-to-Head", value: "1v1" },
  ];

  function setPollMode(e) {
    setConfigState(e);

    let fakeEvent;
    if (config.pollsType === "1v1") {
      fakeEvent = {
        target: { name: "pollsBackgroundColor", value: "default-red" },
      };
    } else {
      fakeEvent = { target: { name: "pollsBackgroundColor", value: "multi" } };
    }

    setConfigState(fakeEvent);
  }

  const demoPollOptions =
    config.pollsType === "multiple"
      ? [
          {
            id: "1",
            name: "Option 1",
            amount_raised: {
              value: "60",
            },
          },
          {
            id: "2",
            name: "Option 2",
            amount_raised: {
              value: "50",
            },
          },
          {
            id: "3",
            name: "Option 3",
            amount_raised: {
              value: "25",
            },
          },
        ]
      : [
          {
            id: "1",
            name: "Option 1",
            amount_raised: {
              value: "25",
            },
          },
          {
            id: "2",
            name: "Option 2",
            amount_raised: {
              value: "25",
            },
          },
        ];

  setConfigState({ target: { name: "charityType", value: CHARITY.TILTIFY } });

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col overflow-x-hidden">
        <div className="example_overlay select-none overflow-hidden w-full min-h-96 flex justify-center">
          <ScaledOverlayWrapper allowScaling={false}>
            <PollOverlay
              options={demoPollOptions}
              name={"My Poll"}
              type={config.pollsType}
              multiBackgroundColor={config.pollsBackgroundColor}
              singleBackgroundColor={toSinglePollOverlayColor(config.pollsBackgroundColor)}
              showPercentage={isYes(config.pollsShowPercentage)}
              showDollars={isYes(config.pollsShowDollars)}
            />
          </ScaledOverlayWrapper>
        </div>

        <div id="customize">
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.pollsBackgroundColor} labelText="Poll color" />

            <ColorPicker
              fieldName="pollsBackgroundColor"
              currentColor={config.pollsBackgroundColor}
              allowMulti={isMultiPoll}
              onChange={setConfigState}
              filterFunction={filterColors}
            />

            <Picker
              items={pollOptions}
              onChange={setPollMode}
              currentValue={config.pollsType}
              fieldName="pollsType"
              labelText="Poll Type"
            />

            <YesNoButtonInputWrapper>
              <YesNoButtonInput
                mode="checkbox"
                className="md:mr-4 mb-2 md:mb-0 flex-1"
                name="pollsShowPercentage"
                label="Show Percentage"
                currentValue={config.pollsShowPercentage}
                onChange={setConfigState}
              />
              <YesNoButtonInput
                className="flex-1"
                mode="checkbox"
                name="pollsShowDollars"
                label="Show Dollars"
                currentValue={config.pollsShowDollars}
                onChange={setConfigState}
              />
            </YesNoButtonInputWrapper>
          </CustomizeSection>
        </div>
      </div>

      <LinkSetup
        allowTeamCampaignSelection={true}
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        showTiltifyPollSelector={true}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        orientation={isMultiPoll ? "horizontal" : "vertical"}
      />
    </div>
  );
}
